import { Link } from "react-router-dom";
import { useEffect } from "react";
import useSWR from "swr";
import { api } from "@/lib/api";
import { trackEventOnce } from "@/lib/analytics";
import { useSettings } from "@/context/SettingsContext";
import Reveal from "@/components/site/Reveal";
import Seo from "@/components/site/Seo";
import PageHeader from "@/components/site/PageHeader";

const fetcher = (url) => api.get(url).then((r) => r.data);

export function PriceTag({ item }) {
  // Il menu pubblico è senza prezzi: si mostra solo l'indicazione "su prenotazione".
  if (item.price_type === "on_request") {
    return (
      <span className="shrink-0 text-[0.62rem] uppercase tracking-[0.16em] text-sage">
        Su prenotazione
      </span>
    );
  }
  return null;
}

export default function MenuPage() {
  useEffect(() => {
    trackEventOnce("view_menu", "view_menu", { menu_section: "menu_completo" });
  }, []);

  const { data: menu, isLoading, error } = useSWR("/menu", fetcher, { revalidateOnFocus: false });
  const { settings } = useSettings();
  const categories = (menu || []).filter((c) => c.items.length > 0);
  const legend = Array.isArray(settings.allergen_legend) ? settings.allergen_legend : [];

  return (
    <>
      <Seo
        title="Menu · Osteria Aterrana, Aterrana di Montoro (AV)"
        description="Il menu dell'Osteria Aterrana: antipasti, primi, secondi, contorni, dolci e vini dell'Irpinia. Cucina tradizionale campana nel borgo di Aterrana."
        path="/menu"
      />
      <PageHeader
        eyebrow="Menu"
        title="Il nostro menu"
        lead={settings.menu_intro || "Cucina tradizionale irpina e campana. Il menu cambia con la stagione."}
        label="DISH PHOTO"
        alt="Un piatto dell'Osteria Aterrana"
        src={settings.menu_header_image}
      />

      <section className="section-y bg-cream">
        <div className="container-ed max-w-4xl">
          {isLoading && <p className="text-sm text-foreground/55">Carichiamo il menu…</p>}
          {error && (
            <div className="border border-wood/20 bg-beige/60 p-8" data-testid="menu-error">
              <p className="font-serif text-2xl text-wood">Il menu non è raggiungibile</p>
              <p className="mt-3 text-sm text-foreground/65">
                Riprova tra poco oppure chiamaci al {settings.phone || "331 10 91 971"}.
              </p>
            </div>
          )}
          {!isLoading && !error && categories.length === 0 && (
            <p className="text-sm text-foreground/60" data-testid="menu-empty">
              Il menu è in aggiornamento. Chiamaci per sapere cosa c'è oggi.
            </p>
          )}

          {categories.length > 0 && (
            <nav className="flex flex-wrap gap-x-6 gap-y-3 pb-12 border-b border-wood/15" aria-label="Categorie">
              {categories.map((c) => (
                <a
                  key={c.id}
                  href={`#${c.slug}`}
                  data-testid={`menu-anchor-${c.slug}`}
                  className="eyebrow text-wood/55 hover:text-sage transition-colors duration-300"
                >
                  {c.name}
                </a>
              ))}
            </nav>
          )}

          <div className="divide-y divide-wood/12" data-testid="menu-list">
            {categories.map((cat) => (
              <section key={cat.id} id={cat.slug} className="py-14 scroll-mt-28">
                <Reveal>
                  <div className="flex items-baseline gap-5">
                    <h2 className="font-serif text-3xl md:text-[2.6rem] text-wood">{cat.name}</h2>
                    <span className="flex-1 h-px bg-wood/15" aria-hidden="true" />
                  </div>
                  {cat.description && (
                    <p className="mt-3 text-sm text-foreground/55 italic">{cat.description}</p>
                  )}
                </Reveal>
                <ul className="mt-9 space-y-8">
                  {cat.items.map((item, i) => (
                    <Reveal as="li" key={item.id} delay={Math.min(i * 0.04, 0.3)}>
                      <div className="flex gap-6">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline gap-4">
                            <h3 className="font-serif text-xl md:text-2xl text-wood">{item.name}</h3>
                            <span className="flex-1 border-b border-dotted border-wood/25" aria-hidden="true" />
                            <PriceTag item={item} />
                          </div>
                          {item.description && (
                            <p className="mt-2 max-w-2xl text-sm md:text-base text-foreground/65 leading-relaxed">
                              {item.description}
                            </p>
                          )}
                          <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs tracking-wider text-foreground/65">
                            {item.price_note && <span>{item.price_note}</span>}
                            {item.reservation_only && (
                              <span className="text-sage">Disponibile solo su prenotazione</span>
                            )}
                            {item.allergens?.length > 0 && <span>Allergeni: {item.allergens.join(", ")}</span>}
                          </div>
                        </div>
                      </div>
                    </Reveal>
                  ))}
                </ul>
              </section>
            ))}
          </div>

          <div className="mt-16 border-t border-wood/15 pt-10">
            {legend.length > 0 && (
              <div className="mb-8" data-testid="allergen-legend">
                <p className="eyebrow text-sage">Allergeni</p>
                <ul className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm text-foreground/65">
                  {legend.map((a, i) => (
                    <li key={a} className="flex items-baseline gap-2">
                      <span className="font-serif text-xs text-gold tabular-nums">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      {a}
                    </li>
                  ))}
                </ul>
                {settings.allergen_note && (
                  <p className="mt-4 text-xs text-foreground/65 max-w-2xl leading-relaxed">
                    {settings.allergen_note}
                  </p>
                )}
              </div>
            )}
            <p className="text-xs text-foreground/65 max-w-2xl leading-relaxed">
              I piatti possono variare in base alla stagione e alla disponibilità dei prodotti. Alcune
              preparazioni possono contenere allergeni: chiedete al personale in sala.
            </p>
            <Link
              to="/prenota"
              className="mt-8 inline-flex px-8 py-4 bg-sage text-cream text-[0.7rem] uppercase tracking-[0.22em] hover:bg-wood transition-colors duration-500"
            >
              Prenota un tavolo
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
